<template>
  <v-container>
    <h1 class="mb-4">Кастомные точки агрегаторов</h1>

    <!-- выбор агрегатора -->
    <v-card class="pa-4 mb-4" elevation="2">
      <v-row align="center">
        <v-col cols="12" md="4">
          <v-select
            v-model="selectedAggregator"
            :items="aggregators"
            item-text="name"
            item-value="code"
            label="Агрегатор"
            :loading="loadingAggregators"
            :disabled="loadingAggregators"
            outlined
            dense
            clearable
          />
        </v-col>

        <v-col cols="12" md="3">
          <v-btn
            color="primary"
            :disabled="!selectedAggregator"
            :loading="loadingPoints"
            @click="loadPoints"
          >
            Обновить точки
          </v-btn>
        </v-col>

        <v-col cols="12" md="5" class="grey--text text--darken-1" style="font-size:12px;">
          Здесь сохраняются точки (склады, ПВЗ и т.п.), привязанные к конкретному агрегатору
          (Достависта, Яндекс и др.). Они потом отдаются по API на карту.
        </v-col>
      </v-row>
    </v-card>

    <!-- форма одиночной точки -->
    <v-card class="pa-4 mb-4" elevation="2">
      <v-card-title class="py-2">Добавить точку</v-card-title>
      <v-card-subtitle class="pb-2">
        Можно указать только адрес (будет геокодирование) или сразу координаты.
      </v-card-subtitle>

      <v-form @submit.prevent="createPoint">
        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="form.title"
              label="Название точки (опционально)"
              dense
              outlined
            />
          </v-col>

          <v-col cols="12" md="8">
            <v-text-field
              v-model="form.address"
              label="Адрес (для геокодирования)"
              dense
              outlined
            />
          </v-col>

          <v-col cols="6" md="3">
            <v-text-field
              v-model="form.lat"
              label="Широта (lat)"
              dense
              outlined
              type="number"
            />
          </v-col>

          <v-col cols="6" md="3">
            <v-text-field
              v-model="form.lon"
              label="Долгота (lon)"
              dense
              outlined
              type="number"
            />
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.meta"
              label="Meta (JSON, опционально)"
              dense
              outlined
              placeholder='Например: {"pvz_id": 123}'
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" class="d-flex justify-end">
            <v-btn
              color="primary"
              type="submit"
              :disabled="!selectedAggregator || !canSubmitSingle"
              :loading="savingSingle"
            >
              Сохранить точку
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>

    <!-- массовая загрузка -->
    <v-card class="pa-4 mb-4" elevation="2">
      <v-card-title class="py-2">Массовая загрузка точек</v-card-title>
      <v-card-subtitle class="pb-2">
        Вставьте список строк формата:<br>
        <code>Название;Адрес;lat;lon</code> — lat/lon можно не указывать, тогда будет геокод по адресу.
      </v-card-subtitle>

      <v-textarea
        v-model="bulkRaw"
        outlined
        rows="6"
        placeholder="Склад №1;Москва, ул. Пушкина, д.10;55.75;37.61
ПВЗ №2;Санкт-Петербург, Невский проспект, 1"
      />

      <div class="d-flex justify-space-between align-center mt-3">
        <div class="grey--text text--darken-1" style="font-size:12px;">
          Пустые строки игнорируются. Ошибочные строки вернутся в разделе результата.
        </div>
        <v-btn
          color="primary"
          :disabled="!selectedAggregator || !bulkRaw.trim()"
          :loading="savingBulk"
          @click="bulkCreatePoints"
        >
          Загрузить список
        </v-btn>
      </div>

      <div v-if="bulkResult" class="mt-3">
        <div>
          Успешно: <strong>{{ bulkResult.inserted_count }}</strong>,
          с ошибками: <strong>{{ bulkResult.failed_count }}</strong>
        </div>
        <ul v-if="bulkResult.failed && bulkResult.failed.length" class="mt-1 red--text" style="font-size:12px;">
          <li v-for="(f, idx) in bulkResult.failed" :key="idx">
            {{ f.reason }}
          </li>
        </ul>
      </div>
    </v-card>

    <!-- таблица точек -->
    <v-card elevation="2">
      <v-card-title class="py-2">
        Точки агрегатора
        <span v-if="currentAggregatorName" class="ml-2">— {{ currentAggregatorName }}</span>
      </v-card-title>

<v-data-table
  :headers="pointHeaders"
  :items="points"
  :loading="loadingPoints"
  item-key="id"
  dense
  class="elevation-1"
>
  <!-- meta-ячейка -->
  <template slot="item.meta" slot-scope="{ item }">
    <span v-if="item.meta">{{ shortMeta(item.meta) }}</span>
    <span v-else class="grey--text text--darken-1">—</span>
  </template>

  <!-- actions-ячейка -->
  <template slot="item.actions" slot-scope="{ item }">
    <v-btn icon small @click="deletePoint(item.id)">
      <v-icon small color="red">mdi-delete</v-icon>
    </v-btn>
  </template>

  <!-- no-data -->
  <template slot="no-data">
    <div class="pa-4 grey--text text--darken-1">
      Нет точек. Выберите агрегатор и загрузите точки.
    </div>
  </template>
</v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AdminMapPoints',
  data () {
    return {
      aggregators: [],
      selectedAggregator: null,

      loadingAggregators: false,
      loadingPoints: false,
      savingSingle: false,
      savingBulk: false,

      form: {
        title: '',
        address: '',
        lat: '',
        lon: '',
        meta: ''
      },

      bulkRaw: '',
      bulkResult: null,

      points: [],
      pointHeaders: [
        { text: 'ID', value: 'id', width: 70 },
        { text: 'Название', value: 'title' },
        { text: 'Адрес', value: 'address' },
        { text: 'Lat', value: 'lat', width: 100 },
        { text: 'Lon', value: 'lon', width: 100 },
        { text: 'Meta', value: 'meta' },
        { text: 'Действия', value: 'actions', sortable: false, width: 80 }
      ]
    }
  },
  computed: {
    currentAggregatorName () {
      const ag = this.aggregators.find(a => a.code === this.selectedAggregator)
      return ag ? ag.name : ''
    },
    canSubmitSingle () {
      // либо адрес, либо lat+lon
      const hasAddress = !!this.form.address.trim()
      const hasCoords = this.form.lat !== '' && this.form.lon !== ''
      return hasAddress || hasCoords
    }
  },
  created () {
    this.loadAggregators()
  },
  methods: {
    showError (msg) {
      if (this.$toast && this.$toast.error) this.$toast.error(msg)
      else alert(msg)
    },

    async loadAggregators () {
      this.loadingAggregators = true
      try {
        const { data } = await axios.get('https://geonode.qpark.io/api/aggregators')
        this.aggregators = Array.isArray(data) ? data : []
      } catch (e) {
        console.error('loadAggregators failed:', e)
        this.showError('Не удалось загрузить список агрегаторов')
      } finally {
        this.loadingAggregators = false
      }
    },

    async loadPoints () {
      if (!this.selectedAggregator) return
      this.loadingPoints = true
      try {
        const { data } = await axios.get('https://geonode.qpark.io/api/map-points', {
          params: { aggregator_code: this.selectedAggregator }
        })
        this.points = (data && data.points) || []
      } catch (e) {
        console.error('loadPoints failed:', e)
        this.showError('Не удалось загрузить точки')
      } finally {
        this.loadingPoints = false
      }
    },

    resetSingleForm () {
      this.form = {
        title: '',
        address: '',
        lat: '',
        lon: '',
        meta: ''
      }
    },

    async createPoint () {
      if (!this.selectedAggregator || !this.canSubmitSingle) return

      let metaParsed = null
      if (this.form.meta && this.form.meta.trim()) {
        try {
          metaParsed = JSON.parse(this.form.meta)
        } catch (e) {
          this.showError('Meta не является корректным JSON')
          return
        }
      }

      const payload = {
        aggregator_code: this.selectedAggregator,
        title: this.form.title || null,
        address: this.form.address || '',
      }

      if (this.form.lat !== '' && this.form.lon !== '') {
        payload.lat = Number(this.form.lat)
        payload.lon = Number(this.form.lon)
      }
      if (metaParsed) payload.meta = metaParsed

      this.savingSingle = true
      try {
        await axios.post('https://geonode.qpark.io/api/map-points', payload)
        this.resetSingleForm()
        await this.loadPoints()
      } catch (e) {
        console.error('createPoint failed:', e)
        this.showError('Не удалось сохранить точку')
      } finally {
        this.savingSingle = false
      }
    },

    async bulkCreatePoints () {
      if (!this.selectedAggregator || !this.bulkRaw.trim()) return

      const lines = this.bulkRaw.split('\n')
      const items = []

      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue

        const parts = trimmed.split(';').map(p => p.trim())
        const [title, address, latStr, lonStr] = parts

        if (!address) {
          // без адреса мы ничем не поможем
          items.push({
            title: title || null,
            address: '',
            _skip: true,
            _reason: 'Нет адреса в строке'
          })
          continue
        }

        const item = { title: title || null, address }

        if (latStr && lonStr && !isNaN(Number(latStr)) && !isNaN(Number(lonStr))) {
          item.lat = Number(latStr)
          item.lon = Number(lonStr)
        }

        items.push(item)
      }

      // отсекаем явно помеченные как _skip
      const validItems = items.filter(i => !i._skip)

      if (!validItems.length) {
        this.showError('Нет валидных строк для загрузки')
        return
      }

      this.savingBulk = true
      try {
        const { data } = await axios.post(
          'https://geonode.qpark.io/api/map-points/bulk',
          {
            aggregator_code: this.selectedAggregator,
            items: validItems
          }
        )
        this.bulkResult = data
        await this.loadPoints()
      } catch (e) {
        console.error('bulkCreatePoints failed:', e)
        this.showError('Не удалось выполнить массовую загрузку')
      } finally {
        this.savingBulk = false
      }
    },

    async deletePoint (id) {
      if (!confirm('Удалить эту точку?')) return
      try {
        await axios.delete(`https://geonode.qpark.io/api/map-points/${id}`)
        await this.loadPoints()
      } catch (e) {
        console.error('deletePoint failed:', e)
        this.showError('Не удалось удалить точку')
      }
    },

    shortMeta (meta) {
      if (typeof meta === 'string') {
        try {
          meta = JSON.parse(meta)
        } catch (e) {
          return meta
        }
      }
      const str = JSON.stringify(meta)
      if (str.length <= 60) return str
      return str.slice(0, 57) + '...'
    }
  }
}
</script>

<style scoped>
h1 {
  font-size: 24px;
  font-weight: 600;
}
code {
  background: #f5f5f5;
  padding: 2px 4px;
  border-radius: 4px;
}
</style>
